import { useState, useEffect } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Org {
  id: string;
  name: string;
  slug: string;
  plan: 'trial' | 'pro' | 'enterprise';
  status: 'active' | 'suspended';
  allowedEmailDomains: string[];
  maxUsers: number;
  maxInitiatives: number;
  maxProducts: number;
  userCount: number;
  initiativeCount: number;
  productCount: number;
  createdAt: string;
  trialExpiresAt: string | null;
}

interface OrgUser {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'EDITOR' | 'VIEWER';
  isActive: boolean;
  inviteToken: string | null;
}

interface UsagePoint {
  month: string;
  users: number;
  initiatives: number;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_ORGS: Org[] = [
  {
    id: '1', name: 'Zuck Group', slug: 'zuck-group', plan: 'pro', status: 'active',
    allowedEmailDomains: ['meta.com', 'facebook.com'],
    maxUsers: 25, maxInitiatives: 200, maxProducts: 20,
    userCount: 8, initiativeCount: 47, productCount: 3,
    createdAt: '2026-02-18T23:51:01.405Z', trialExpiresAt: null,
  },
  {
    id: '2', name: 'Franco', slug: 'franco', plan: 'trial', status: 'active',
    allowedEmailDomains: [],
    maxUsers: 5, maxInitiatives: 50, maxProducts: 5,
    userCount: 2, initiativeCount: 8, productCount: 1,
    createdAt: '2026-02-20T22:48:28.548Z', trialExpiresAt: '2026-03-20T22:48:28.548Z',
  },
  {
    id: '3', name: 'Acme Corp', slug: 'acme', plan: 'enterprise', status: 'active',
    allowedEmailDomains: ['acme.com'],
    maxUsers: 100, maxInitiatives: 1000, maxProducts: 50,
    userCount: 34, initiativeCount: 212, productCount: 8,
    createdAt: '2026-01-05T10:00:00.000Z', trialExpiresAt: null,
  },
  {
    id: '4', name: 'Startup XYZ', slug: 'startupxyz', plan: 'trial', status: 'suspended',
    allowedEmailDomains: [],
    maxUsers: 5, maxInitiatives: 50, maxProducts: 5,
    userCount: 1, initiativeCount: 3, productCount: 1,
    createdAt: '2026-02-10T08:00:00.000Z', trialExpiresAt: '2026-02-17T08:00:00.000Z',
  },
];

const MOCK_USERS: Record<string, OrgUser[]> = {
  '1': [
    { id: 'u1', name: 'Mark Z', email: 'mark@meta.com', role: 'ADMIN', isActive: true, inviteToken: null },
    { id: 'u2', name: 'Sheryl S', email: 'sheryl@meta.com', role: 'EDITOR', isActive: true, inviteToken: null },
    { id: 'u3', name: 'New Hire', email: 'newhire@meta.com', role: 'VIEWER', isActive: false, inviteToken: 'abc123' },
  ],
  '2': [
    { id: 'u4', name: 'Franco B', email: 'franco@gmail.com', role: 'ADMIN', isActive: true, inviteToken: null },
    { id: 'u5', name: 'Team Member', email: 'team@gmail.com', role: 'VIEWER', isActive: false, inviteToken: 'xyz789' },
  ],
  '3': [
    { id: 'u6', name: 'Alice Admin', email: 'alice@acme.com', role: 'ADMIN', isActive: true, inviteToken: null },
  ],
  '4': [
    { id: 'u7', name: 'Bob Builder', email: 'bob@startupxyz.com', role: 'ADMIN', isActive: true, inviteToken: null },
  ],
};

const MOCK_USAGE: Record<string, UsagePoint[]> = {
  '1': [
    { month: 'Sep', users: 2, initiatives: 10 },
    { month: 'Oct', users: 3, initiatives: 18 },
    { month: 'Nov', users: 5, initiatives: 27 },
    { month: 'Dec', users: 6, initiatives: 35 },
    { month: 'Jan', users: 7, initiatives: 41 },
    { month: 'Feb', users: 8, initiatives: 47 },
  ],
  '2': [
    { month: 'Sep', users: 0, initiatives: 0 },
    { month: 'Oct', users: 0, initiatives: 0 },
    { month: 'Nov', users: 0, initiatives: 0 },
    { month: 'Dec', users: 0, initiatives: 0 },
    { month: 'Jan', users: 1, initiatives: 3 },
    { month: 'Feb', users: 2, initiatives: 8 },
  ],
  '3': [
    { month: 'Sep', users: 15, initiatives: 80 },
    { month: 'Oct', users: 20, initiatives: 120 },
    { month: 'Nov', users: 25, initiatives: 150 },
    { month: 'Dec', users: 28, initiatives: 175 },
    { month: 'Jan', users: 31, initiatives: 195 },
    { month: 'Feb', users: 34, initiatives: 212 },
  ],
  '4': [
    { month: 'Sep', users: 0, initiatives: 0 },
    { month: 'Oct', users: 0, initiatives: 0 },
    { month: 'Nov', users: 0, initiatives: 0 },
    { month: 'Dec', users: 0, initiatives: 0 },
    { month: 'Jan', users: 1, initiatives: 2 },
    { month: 'Feb', users: 1, initiatives: 3 },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diffDays = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const m = Math.floor(diffDays / 30);
  return m === 1 ? '1 month ago' : `${m} months ago`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isExpired(dateStr: string | null): boolean {
  return !!dateStr && new Date(dateStr) < new Date();
}

// ─── Badge Components ─────────────────────────────────────────────────────────

function PlanBadge({ plan }: { plan: Org['plan'] }) {
  const s: Record<string, { color: string; bg: string; border: string }> = {
    trial:      { color: '#92400E', bg: '#FFFBEB', border: '#FDE68A' },
    pro:        { color: '#1E40AF', bg: '#EFF6FF', border: '#BFDBFE' },
    enterprise: { color: '#5B21B6', bg: '#F5F3FF', border: '#DDD6FE' },
  };
  const { color, bg, border } = s[plan];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: '4px',
      fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
      color, backgroundColor: bg, border: `1px solid ${border}`,
    }}>
      {plan}
    </span>
  );
}

function StatusBadge({ status }: { status: Org['status'] }) {
  const active = status === 'active';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '2px 8px', borderRadius: '4px',
      fontSize: '11px', fontWeight: 600,
      color: active ? '#065F46' : '#991B1B',
      backgroundColor: active ? '#ECFDF5' : '#FEF2F2',
      border: `1px solid ${active ? '#A7F3D0' : '#FECACA'}`,
    }}>
      <span style={{
        width: '5px', height: '5px', borderRadius: '50%',
        backgroundColor: active ? '#10B981' : '#EF4444',
        display: 'inline-block',
        boxShadow: active ? '0 0 0 2px #D1FAE5' : '0 0 0 2px #FEE2E2',
      }} />
      {active ? 'Active' : 'Suspended'}
    </span>
  );
}

function UsageCount({ count, max }: { count: number; max: number }) {
  const pct = max > 0 ? count / max : 0;
  const color = pct >= 1 ? '#DC2626' : pct >= 0.8 ? '#D97706' : '#3F3F46';
  const weight = pct >= 0.8 ? 600 : 400;
  return (
    <span style={{ fontSize: '13px', color, fontWeight: weight, fontVariantNumeric: 'tabular-nums' }}>
      {count}
      <span style={{ color: '#D4D4D8', fontWeight: 400 }}> / {max}</span>
    </span>
  );
}

// ─── Bar Chart ────────────────────────────────────────────────────────────────

function BarChart({ data }: { data: UsagePoint[] }) {
  const maxI = Math.max(...data.map(d => d.initiatives), 1);
  const maxU = Math.max(...data.map(d => d.users), 1);
  const chartH = 72;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: `${chartH}px` }}>
        {data.map((pt, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: `${chartH}px` }}>
              <div style={{
                flex: 1, borderRadius: '3px 3px 0 0',
                backgroundColor: '#F59E0B', opacity: 0.85,
                height: `${Math.max((pt.initiatives / maxI) * chartH, pt.initiatives > 0 ? 3 : 0)}px`,
                transition: 'height 0.4s ease',
              }} />
              <div style={{
                flex: 1, borderRadius: '3px 3px 0 0',
                backgroundColor: '#10B981', opacity: 0.85,
                height: `${Math.max((pt.users / maxU) * chartH, pt.users > 0 ? 3 : 0)}px`,
                transition: 'height 0.4s ease',
              }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
        {data.map((pt, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center' }}>
            <span style={{ fontSize: '10px', color: '#A1A1AA' }}>{pt.month}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '16px', marginTop: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: '#F59E0B' }} />
          <span style={{ fontSize: '11px', color: '#71717A' }}>Initiatives</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: '#10B981' }} />
          <span style={{ fontSize: '11px', color: '#71717A' }}>Users</span>
        </div>
      </div>
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: '10px', fontWeight: 700, letterSpacing: '0.09em',
      textTransform: 'uppercase', color: '#A1A1AA', marginBottom: '10px',
    }}>
      {children}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function PlatformAdminPage() {
  const [orgs, setOrgs] = useState<Org[]>(MOCK_ORGS);
  const [selectedOrg, setSelectedOrg] = useState<Org | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [limitEdits, setLimitEdits] = useState({ maxUsers: 0, maxInitiatives: 0, maxProducts: 0 });
  const [limitSaved, setLimitSaved] = useState(false);

  // Inject Google Fonts
  useEffect(() => {
    const id = 'platform-admin-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Mono:ital,wght@0,400;0,500;1,400&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  // Stats
  const now = new Date();
  const totalOrgs = orgs.length;
  const activeOrgs = orgs.filter(o => o.status === 'active').length;
  const totalUsers = orgs.reduce((sum, o) => sum + o.userCount, 0);
  const newThisMonth = orgs.filter(o => {
    const d = new Date(o.createdAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  // Always read from orgs state so drawer reflects latest toggles
  const currentOrg = selectedOrg ? (orgs.find(o => o.id === selectedOrg.id) ?? null) : null;

  const openDrawer = (org: Org) => {
    setSelectedOrg(org);
    setLimitEdits({ maxUsers: org.maxUsers, maxInitiatives: org.maxInitiatives, maxProducts: org.maxProducts });
    setLimitSaved(false);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedOrg(null), 300);
  };

  const toggleStatus = (orgId: string) => {
    setOrgs(prev => prev.map(o =>
      o.id === orgId ? { ...o, status: o.status === 'active' ? 'suspended' : 'active' } : o
    ));
  };

  const saveLimits = () => {
    if (!selectedOrg) return;
    setOrgs(prev => prev.map(o => o.id === selectedOrg.id ? { ...o, ...limitEdits } : o));
    setLimitSaved(true);
    setTimeout(() => setLimitSaved(false), 2000);
  };

  const FONT = "'Syne', system-ui, sans-serif";
  const MONO = "'DM Mono', 'Courier New', monospace";

  const stats = [
    { label: 'Total Orgs',    value: totalOrgs,     accent: '#6366F1' },
    { label: 'Active',        value: activeOrgs,    accent: '#10B981' },
    { label: 'Total Users',   value: totalUsers,    accent: '#F59E0B' },
    { label: 'New This Month',value: newThisMonth,  accent: '#3B82F6' },
  ];

  const TH_STYLE: React.CSSProperties = {
    padding: '10px 16px',
    textAlign: 'left' as const,
    fontSize: '10px', fontWeight: 700,
    color: '#A1A1AA', letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    backgroundColor: '#FAFAFA',
    whiteSpace: 'nowrap' as const,
  };

  return (
    <div style={{ fontFamily: FONT, minHeight: '100vh', backgroundColor: '#F4F3F0' }}>
      {/* ── Navbar ─────────────────────────────────────────────────────────── */}
      <header style={{
        backgroundColor: '#0C0C0E',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        position: 'sticky', top: 0, zIndex: 30,
      }}>
        <div style={{
          maxWidth: '1440px', margin: '0 auto',
          padding: '0 28px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: '54px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '26px', height: '26px',
              background: 'linear-gradient(135deg, #F59E0B, #EF4444)',
              borderRadius: '6px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ color: '#fff', fontWeight: 800, fontSize: '12px' }}>P</span>
            </div>
            <span style={{ color: '#FFFFFF', fontWeight: 700, fontSize: '14px', letterSpacing: '-0.3px' }}>
              Praxislab
            </span>
            <span style={{
              color: '#52525B', fontSize: '13px',
              borderLeft: '1px solid #27272A', paddingLeft: '10px', marginLeft: '2px',
            }}>
              Platform Admin
            </span>
          </div>
          <button style={{
            color: '#71717A', background: 'none', border: '1px solid #27272A',
            borderRadius: '6px', cursor: 'pointer', padding: '5px 12px',
            fontSize: '12px', fontWeight: 500, fontFamily: FONT,
            transition: 'all 0.15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.color = '#D4D4D8'; e.currentTarget.style.borderColor = '#3F3F46'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#71717A'; e.currentTarget.style.borderColor = '#27272A'; }}
          >
            Sign out
          </button>
        </div>
      </header>

      {/* ── Main ───────────────────────────────────────────────────────────── */}
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '36px 28px' }}>

        {/* Page title */}
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#18181B', letterSpacing: '-0.5px' }}>
            Organizations
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#71717A' }}>
            {totalOrgs} workspaces · {totalUsers} users across the platform
          </p>
        </div>

        {/* ── Stats ─────────────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '24px' }}>
          {stats.map(s => (
            <div key={s.label} style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '10px',
              padding: '18px 22px',
              border: '1px solid #E4E4E7',
              borderTop: `3px solid ${s.accent}`,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}>
              <div style={{ fontSize: '11px', color: '#A1A1AA', fontWeight: 600, marginBottom: '10px', letterSpacing: '0.03em' }}>
                {s.label}
              </div>
              <div style={{ fontSize: '36px', fontWeight: 800, color: '#18181B', letterSpacing: '-1.5px', lineHeight: 1 }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* ── Orgs Table ────────────────────────────────────────────────── */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          border: '1px solid #E4E4E7',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #F4F4F5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#18181B' }}>All Organizations</span>
            <span style={{ fontSize: '12px', color: '#A1A1AA' }}>{orgs.length} total</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #F4F4F5' }}>
                  <th style={TH_STYLE}>Organization</th>
                  <th style={TH_STYLE}>Plan</th>
                  <th style={TH_STYLE}>Status</th>
                  <th style={TH_STYLE}>Users</th>
                  <th style={TH_STYLE}>Initiatives</th>
                  <th style={TH_STYLE}>Products</th>
                  <th style={TH_STYLE}>Created</th>
                  <th style={TH_STYLE}>Trial Expires</th>
                  <th style={{ ...TH_STYLE, textAlign: 'right' as const }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map((org, i) => (
                  <tr
                    key={org.id}
                    style={{
                      borderBottom: i < orgs.length - 1 ? '1px solid #F9F9F9' : 'none',
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#FAFAFA')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <td style={{ padding: '13px 16px' }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#18181B' }}>{org.name}</div>
                      <div style={{ fontFamily: MONO, fontSize: '11px', color: '#A1A1AA', marginTop: '2px' }}>{org.slug}</div>
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <PlanBadge plan={org.plan} />
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <StatusBadge status={org.status} />
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <UsageCount count={org.userCount} max={org.maxUsers} />
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', color: '#3F3F46', fontVariantNumeric: 'tabular-nums' }}>
                      {org.initiativeCount}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '13px', color: '#3F3F46', fontVariantNumeric: 'tabular-nums' }}>
                      {org.productCount}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '12px', color: '#71717A' }}>
                      {timeAgo(org.createdAt)}
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      {org.trialExpiresAt ? (
                        <span style={{
                          fontSize: '12px',
                          color: isExpired(org.trialExpiresAt) ? '#DC2626' : '#71717A',
                          fontWeight: isExpired(org.trialExpiresAt) ? 600 : 400,
                        }}>
                          {isExpired(org.trialExpiresAt) && '⚠ '}{formatDate(org.trialExpiresAt)}
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#D4D4D8' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => openDrawer(org)}
                          style={{
                            padding: '4px 11px', fontSize: '11px', fontWeight: 600,
                            color: '#3F3F46', background: 'transparent',
                            border: '1px solid #E4E4E7', borderRadius: '6px',
                            cursor: 'pointer', fontFamily: FONT, transition: 'all 0.1s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#A1A1AA'; e.currentTarget.style.color = '#18181B'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = '#E4E4E7'; e.currentTarget.style.color = '#3F3F46'; }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => toggleStatus(org.id)}
                          style={{
                            padding: '4px 11px', fontSize: '11px', fontWeight: 600,
                            color: org.status === 'active' ? '#B91C1C' : '#065F46',
                            background: 'transparent',
                            border: `1px solid ${org.status === 'active' ? '#FECACA' : '#A7F3D0'}`,
                            borderRadius: '6px', cursor: 'pointer', fontFamily: FONT,
                            transition: 'all 0.1s',
                          }}
                        >
                          {org.status === 'active' ? 'Suspend' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* ── Backdrop ───────────────────────────────────────────────────────── */}
      <div
        onClick={closeDrawer}
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.35)',
          backdropFilter: 'blur(3px)',
          zIndex: 40,
          opacity: drawerOpen ? 1 : 0,
          pointerEvents: drawerOpen ? 'auto' : 'none',
          transition: 'opacity 0.25s ease',
        }}
      />

      {/* ── Detail Drawer ──────────────────────────────────────────────────── */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: '500px',
        backgroundColor: '#FFFFFF',
        boxShadow: '-12px 0 40px rgba(0,0,0,0.10)',
        zIndex: 50,
        overflowY: 'auto',
        transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
        fontFamily: FONT,
      }}>
        {currentOrg && (
          <>
            {/* Drawer top bar */}
            <div style={{
              position: 'sticky', top: 0, zIndex: 10,
              backgroundColor: '#FFFFFF',
              borderBottom: '1px solid #F4F4F5',
              padding: '18px 22px',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '5px' }}>
                  <span style={{ fontSize: '17px', fontWeight: 800, color: '#18181B', letterSpacing: '-0.4px' }}>
                    {currentOrg.name}
                  </span>
                  <PlanBadge plan={currentOrg.plan} />
                  <StatusBadge status={currentOrg.status} />
                </div>
                <span style={{ fontFamily: MONO, fontSize: '11px', color: '#A1A1AA' }}>{currentOrg.slug}</span>
              </div>
              <button
                onClick={closeDrawer}
                style={{
                  flexShrink: 0,
                  width: '28px', height: '28px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#A1A1AA', background: 'none',
                  border: '1px solid #F4F4F5', borderRadius: '6px',
                  cursor: 'pointer', fontSize: '16px', lineHeight: 1,
                  transition: 'all 0.1s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#18181B'; e.currentTarget.style.borderColor = '#E4E4E7'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#A1A1AA'; e.currentTarget.style.borderColor = '#F4F4F5'; }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: '22px' }}>

              {/* ── Info grid ── */}
              <section>
                <SectionLabel>Details</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {[
                    { label: 'Workspace URL', value: `${currentOrg.slug}.tabillo.com`, mono: true },
                    { label: 'Plan', value: currentOrg.plan.charAt(0).toUpperCase() + currentOrg.plan.slice(1) },
                    { label: 'Created', value: formatDate(currentOrg.createdAt) },
                    { label: 'Trial Expires', value: currentOrg.trialExpiresAt ? formatDate(currentOrg.trialExpiresAt) : '—' },
                  ].map(item => (
                    <div key={item.label} style={{
                      backgroundColor: '#FAFAFA',
                      borderRadius: '8px', padding: '11px 13px',
                      border: '1px solid #F0F0F0',
                    }}>
                      <div style={{ fontSize: '10px', color: '#A1A1AA', fontWeight: 600, marginBottom: '4px', letterSpacing: '0.04em' }}>
                        {item.label}
                      </div>
                      <div style={{
                        fontSize: '12px', color: '#18181B', fontWeight: 500,
                        fontFamily: item.mono ? MONO : FONT,
                        wordBreak: 'break-all',
                      }}>
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Allowed domains */}
                <div style={{
                  marginTop: '10px', backgroundColor: '#FAFAFA',
                  borderRadius: '8px', padding: '11px 13px', border: '1px solid #F0F0F0',
                }}>
                  <div style={{ fontSize: '10px', color: '#A1A1AA', fontWeight: 600, marginBottom: '7px', letterSpacing: '0.04em' }}>
                    Allowed Email Domains
                  </div>
                  {currentOrg.allowedEmailDomains.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                      {currentOrg.allowedEmailDomains.map(d => (
                        <span key={d} style={{
                          fontFamily: MONO, fontSize: '11px',
                          color: '#3F3F46', backgroundColor: '#F4F4F5',
                          border: '1px solid #E4E4E7', borderRadius: '4px',
                          padding: '2px 8px',
                        }}>
                          @{d}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize: '12px', color: '#10B981', fontWeight: 600 }}>
                      Any domain allowed
                    </span>
                  )}
                </div>
              </section>

              {/* ── Usage Chart ── */}
              <section>
                <SectionLabel>Usage — last 6 months</SectionLabel>
                <div style={{
                  backgroundColor: '#FAFAFA', borderRadius: '10px',
                  padding: '16px', border: '1px solid #F0F0F0',
                }}>
                  <BarChart data={MOCK_USAGE[currentOrg.id] ?? []} />
                </div>
              </section>

              {/* ── Limits ── */}
              <section>
                <SectionLabel>Usage Limits</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                  {[
                    { key: 'maxUsers' as const, label: 'Max Users', current: currentOrg.userCount },
                    { key: 'maxInitiatives' as const, label: 'Max Initiatives', current: currentOrg.initiativeCount },
                    { key: 'maxProducts' as const, label: 'Max Products', current: currentOrg.productCount },
                  ].map(item => (
                    <div key={item.key} style={{
                      backgroundColor: '#FAFAFA', borderRadius: '8px',
                      padding: '11px 12px', border: '1px solid #F0F0F0',
                    }}>
                      <div style={{ fontSize: '10px', color: '#A1A1AA', fontWeight: 600, marginBottom: '4px' }}>{item.label}</div>
                      <div style={{ fontSize: '10px', color: '#D4D4D8', marginBottom: '6px' }}>
                        Current: {item.current}
                      </div>
                      <input
                        type="number"
                        value={limitEdits[item.key]}
                        onChange={e => setLimitEdits(prev => ({
                          ...prev,
                          [item.key]: parseInt(e.target.value) || 0,
                        }))}
                        style={{
                          width: '100%', padding: '5px 8px',
                          fontSize: '15px', fontWeight: 700, color: '#18181B',
                          border: '1px solid #E4E4E7', borderRadius: '6px',
                          backgroundColor: '#FFFFFF', outline: 'none',
                          fontFamily: FONT, boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={saveLimits}
                  style={{
                    padding: '8px 18px', fontSize: '12px', fontWeight: 700,
                    color: limitSaved ? '#065F46' : '#FFFFFF',
                    backgroundColor: limitSaved ? '#ECFDF5' : '#18181B',
                    border: `1px solid ${limitSaved ? '#A7F3D0' : '#18181B'}`,
                    borderRadius: '7px', cursor: 'pointer',
                    fontFamily: FONT, transition: 'all 0.2s', letterSpacing: '0.01em',
                  }}
                >
                  {limitSaved ? '✓ Saved' : 'Save Limits'}
                </button>
              </section>

              {/* ── Users ── */}
              <section>
                <SectionLabel>
                  Users ({(MOCK_USERS[currentOrg.id] ?? []).length})
                </SectionLabel>
                <div style={{ border: '1px solid #F0F0F0', borderRadius: '10px', overflow: 'hidden' }}>
                  {(MOCK_USERS[currentOrg.id] ?? []).map((user, i, arr) => (
                    <div key={user.id} style={{
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      borderBottom: i < arr.length - 1 ? '1px solid #F9F9F9' : 'none',
                      backgroundColor: '#FFFFFF',
                    }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#18181B' }}>{user.name}</span>
                          {user.inviteToken && (
                            <span style={{
                              fontSize: '9px', fontWeight: 700, letterSpacing: '0.07em',
                              color: '#92400E', backgroundColor: '#FEF3C7',
                              border: '1px solid #FDE68A', borderRadius: '3px', padding: '1px 5px',
                              textTransform: 'uppercase',
                            }}>
                              Pending
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '11px', color: '#A1A1AA', marginTop: '1px', fontFamily: MONO }}>{user.email}</div>
                      </div>
                      <span style={{
                        fontSize: '10px', fontWeight: 700, padding: '3px 8px',
                        borderRadius: '4px', letterSpacing: '0.05em',
                        ...(user.role === 'ADMIN'
                          ? { color: '#5B21B6', backgroundColor: '#F5F3FF', border: '1px solid #DDD6FE' }
                          : user.role === 'EDITOR'
                          ? { color: '#1E40AF', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }
                          : { color: '#71717A', backgroundColor: '#F4F4F5', border: '1px solid #E4E4E7' }),
                      }}>
                        {user.role}
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── Danger Zone ── */}
              <section style={{ borderTop: '1px solid #F4F4F5', paddingTop: '18px' }}>
                <SectionLabel>Organization Actions</SectionLabel>
                <button
                  onClick={() => toggleStatus(currentOrg.id)}
                  style={{
                    padding: '8px 18px', fontSize: '12px', fontWeight: 700,
                    color: currentOrg.status === 'active' ? '#991B1B' : '#065F46',
                    backgroundColor: currentOrg.status === 'active' ? '#FEF2F2' : '#ECFDF5',
                    border: `1px solid ${currentOrg.status === 'active' ? '#FECACA' : '#A7F3D0'}`,
                    borderRadius: '7px', cursor: 'pointer',
                    fontFamily: FONT, transition: 'all 0.15s',
                  }}
                >
                  {currentOrg.status === 'active' ? 'Suspend Organization' : 'Activate Organization'}
                </button>
              </section>

            </div>
          </>
        )}
      </div>

      <style>{`
        * { box-sizing: border-box; }
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button { opacity: 1; }
      `}</style>
    </div>
  );
}
